import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, FileText, Image, Download, Leaf, Play, ExternalLink } from 'lucide-react';
import { formatFullDateIST } from '../../lib/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getEmbedInfo(url) {
  if (!url) return null;
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return { type: 'youtube', id: ytMatch[1], embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}` };
  // Instagram
  const igMatch = url.match(/instagram\.com\/(?:p|reel|tv)\/([a-zA-Z0-9_-]+)/);
  if (igMatch) return { type: 'instagram', id: igMatch[1], embedUrl: `https://www.instagram.com/p/${igMatch[1]}/embed` };
  return null;
}

export default function SharedMediaPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const { data: resp } = await axios.get(`${API_URL}/api/shared/media/${token}`);
        setData(resp);
      } catch (err) {
        setError(err.response?.data?.detail || 'This link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8F6]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0E5132]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8F6]">
        <Card className="max-w-sm border-red-200">
          <CardContent className="py-8 text-center">
            <p className="text-red-600 font-medium">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const media = data?.media || [];
  const links = data?.links || [];

  return (
    <div className="min-h-screen bg-[#F7F8F6] py-8 px-4" data-testid="shared-media-page">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-[12px] bg-[#0E5132] flex items-center justify-center">
            <Leaf className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-lg text-[#0E5132]">Eden Skin Clinic</h1>
            <p className="text-xs text-slate-500">Shared Medical Files &amp; Resources</p>
          </div>
        </div>

        {/* Patient info */}
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent className="py-3 px-4">
            <p className="font-semibold text-sm text-[#0E5132]">{data.patient_name}</p>
            <p className="text-xs text-slate-500">{data.date ? formatFullDateIST(data.date) : ''}</p>
          </CardContent>
        </Card>

        {/* Video / External Links */}
        {links.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Videos &amp; Links</p>
            {links.map((link, i) => {
              const embed = getEmbedInfo(link.url);
              return (
                <Card key={i} className="border-slate-200 overflow-hidden" data-testid={`shared-link-${i}`}>
                  {embed?.type === 'youtube' && (
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                      <iframe
                        src={embed.embedUrl}
                        title={link.title || 'Video'}
                        className="absolute inset-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                  {embed?.type === 'instagram' && (
                    <div className="relative w-full bg-white" style={{ minHeight: 400 }}>
                      <iframe
                        src={embed.embedUrl}
                        title={link.title || 'Instagram Post'}
                        className="w-full border-0"
                        style={{ minHeight: 400 }}
                        scrolling="no"
                      />
                    </div>
                  )}
                  <CardContent className="py-3 px-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {embed?.type === 'youtube' ? (
                        <Play className="w-4 h-4 text-red-600" />
                      ) : embed?.type === 'instagram' ? (
                        <div className="w-4 h-4 rounded bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400" />
                      ) : (
                        <ExternalLink className="w-4 h-4 text-blue-600" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-slate-700">{link.title || 'External Link'}</p>
                        <p className="text-[10px] text-slate-400">{link.added_by}</p>
                      </div>
                    </div>
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[#0E5132] hover:underline">
                      <ExternalLink className="w-3.5 h-3.5" /> Open
                    </a>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Uploaded Files */}
        {media.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Files</p>
            {media.map((m, i) => {
              const isImage = m.content_type?.startsWith('image/');
              const fileUrl = `${API_URL}/api/shared/media-file/${m.filename}?token=${token}`;
              return (
                <Card key={i} className="border-slate-200 overflow-hidden" data-testid={`shared-media-${i}`}>
                  {isImage && (
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                      <img src={fileUrl} alt={m.original_name} className="w-full max-h-80 object-contain bg-slate-50" />
                    </a>
                  )}
                  <CardContent className="py-3 px-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isImage ? <Image className="w-4 h-4 text-emerald-600" /> : <FileText className="w-4 h-4 text-blue-600" />}
                      <div>
                        <p className="text-sm font-medium text-slate-700">{m.original_name}</p>
                        <p className="text-[10px] text-slate-400">
                          {m.uploaded_by} &middot; {(m.size / 1024).toFixed(0)} KB
                        </p>
                      </div>
                    </div>
                    <a href={fileUrl} download={m.original_name} className="flex items-center gap-1 text-xs text-[#0E5132] hover:underline">
                      <Download className="w-3.5 h-3.5" /> Download
                    </a>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {media.length === 0 && links.length === 0 && (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="py-8 text-center">
              <p className="text-sm text-muted-foreground">No media files or links shared</p>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-[10px] text-slate-400 pt-4">
          This link expires in 90 days from the prescription date.
        </p>
      </div>
    </div>
  );
}
